import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { AppShell, PageHeader } from '@/components/AppShell';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  PanelLeftOpen,
  Plus,
  Pencil,
  Trash2,
  Play,
  Loader2,
  MessageSquare,
} from 'lucide-react';
import {
  listSlackBots,
  createSlackBot,
  updateSlackBot,
  deleteSlackBot,
  testSlackBot,
  type SlackBot,
  type CreateSlackBotPayload,
  type UpdateSlackBotPayload,
} from '@/services/slack-bots-api';

interface SlackBotFormState {
  name: string;
  channel_id: string;
  channel_name: string;
}

export default function SlackBotsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SlackBot | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<SlackBot | null>(null);

  const { data: bots = [], isLoading } = useQuery({
    queryKey: ['slack-bots'],
    queryFn: listSlackBots,
  });

  const createMut = useMutation({
    mutationFn: (payload: CreateSlackBotPayload) => createSlackBot(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slack-bots'] });
      toast.success('Slack channel added');
      setCreating(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSlackBotPayload }) => updateSlackBot(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slack-bots'] });
      toast.success('Slack channel updated');
      setEditing(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteSlackBot(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slack-bots'] });
      toast.success('Slack channel removed');
      setConfirmDelete(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMut = useMutation({
    mutationFn: (id: string) => testSlackBot(id),
    onSuccess: (data) => toast.success(data.message),
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      updateSlackBot(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['slack-bots'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const sorted = useMemo(() => bots.slice().sort((a, b) => a.name.localeCompare(b.name)), [bots]);

  return (
    <AppShell>
      <div className="flex flex-col h-full">
        <PageHeader>
            <SidebarTrigger>
              <PanelLeftOpen className="h-4 w-4" />
            </SidebarTrigger>
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold leading-tight">Slack Bots</h1>
              <p className="text-xs text-muted-foreground">
                Configure channels to receive notifications for agentic group sessions. Uses the bot token from <code className="text-[11px] bg-muted px-1 py-0.5 rounded">SLACK_BOT_TOKEN</code>.
              </p>
            </div>
            <div className="ml-auto">
              <Button size="sm" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add channel
              </Button>
            </div>
        </PageHeader>

          <div className="flex-1 overflow-auto px-6 py-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading channels…
              </div>
            ) : sorted.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-20 text-center">
                <MessageSquare className="h-8 w-8 text-muted-foreground/40" />
                <div className="text-sm font-medium">No Slack channels configured</div>
                <p className="text-xs text-muted-foreground max-w-sm">
                  Add a channel to receive start and completion notifications when you run agentic group sessions.
                </p>
              </div>
            ) : (
              <div className="border rounded-lg divide-y">
                {sorted.map((bot) => (
                  <div key={bot.id} className="flex items-center justify-between gap-4 px-4 py-4 hover:bg-muted/30 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h2 className="text-sm font-semibold truncate">{bot.name}</h2>
                        <span className={bot.enabled ? "inline-flex items-center gap-1.5 text-xs text-green-600" : "inline-flex items-center gap-1.5 text-xs text-muted-foreground"}>
                          <span className={bot.enabled ? "h-1.5 w-1.5 rounded-full bg-green-500" : "h-1.5 w-1.5 rounded-full bg-muted-foreground/40"} />
                          {bot.enabled ? 'active' : 'disabled'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">
                        {bot.channel_name || bot.channel_id}
                        {bot.channel_name && <span className="text-muted-foreground/60"> ({bot.channel_id})</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Switch
                        checked={bot.enabled}
                        onCheckedChange={(checked) => toggleMut.mutate({ id: bot.id, enabled: checked })}
                      />
                      <Button variant="ghost" size="sm" onClick={() => testMut.mutate(bot.id)} disabled={testMut.isPending}>
                        {testMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(bot)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(bot)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      {creating && (
        <SlackBotFormDialog
          open
          title="Add Slack channel"
          submitLabel="Add"
          isSubmitting={createMut.isPending}
          onClose={() => setCreating(false)}
          onSubmit={(state) => createMut.mutate({
            name: state.name,
            channel_id: state.channel_id,
            channel_name: state.channel_name || undefined,
          })}
        />
      )}

      {editing && (
        <SlackBotFormDialog
          open
          title={`Edit ${editing.name}`}
          submitLabel="Save"
          initial={{
            name: editing.name,
            channel_id: editing.channel_id,
            channel_name: editing.channel_name || '',
          }}
          isSubmitting={updateMut.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(state) => updateMut.mutate({
            id: editing.id,
            payload: {
              name: state.name,
              channel_id: state.channel_id,
              channel_name: state.channel_name || undefined,
            },
          })}
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This channel will no longer receive notifications. Existing session data is preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMut.isPending}
              onClick={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
            >
              {deleteMut.isPending ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

function SlackBotFormDialog({
  open,
  title,
  submitLabel,
  initial,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  submitLabel: string;
  initial?: SlackBotFormState;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (state: SlackBotFormState) => void;
}) {
  const [state, setState] = useState<SlackBotFormState>(
    initial ?? { name: '', channel_id: '', channel_name: '' },
  );

  const valid = state.name.trim().length > 0 && state.channel_id.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Each entry maps a Slack channel to receive notifications. The bot token is shared from <code className="text-[11px] bg-muted px-1 py-0.5 rounded">SLACK_BOT_TOKEN</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="sb-name">Name</Label>
            <Input
              id="sb-name"
              value={state.name}
              onChange={(e) => setState({ ...state, name: e.target.value })}
              placeholder="e.g. Acme Corp"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sb-channel-id">Channel ID</Label>
            <Input
              id="sb-channel-id"
              value={state.channel_id}
              onChange={(e) => setState({ ...state, channel_id: e.target.value })}
              placeholder="D0B3E4XHTQX or C07XYZ123"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Right-click channel in Slack → Copy link → extract ID from the URL.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sb-channel-name">Channel name (for display)</Label>
            <Input
              id="sb-channel-name"
              value={state.channel_name}
              onChange={(e) => setState({ ...state, channel_name: e.target.value })}
              placeholder="#acme-shared"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(state)} disabled={!valid || isSubmitting}>
            {isSubmitting ? 'Saving…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
